import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
   DashboardAutoCompletedTaskResponse,
   DashboardAutoCompletionResponse
} from '../../../services/response/user-property-dashboard-response';
import { DashboardCardComponent } from '../dashboard-card/dashboard-card.component';

interface AutoCompletedTaskGroup {
   reportTypeCode: string;
   reportTypeName: string;
   count: number;
   reasons: string[];
}

interface AutoCompletionOutcomeRow {
   label: string;
   value: number;
   className: string;
}

@Component({
   selector: 'app-dashboard-auto-completed-tasks',
   standalone: true,
   imports: [CommonModule, DashboardCardComponent],
   templateUrl: './auto-completed-tasks.component.html',
   styleUrls: ['./auto-completed-tasks.component.scss']
})
export class AutoCompletedTasksComponent {
   @Input() autoCompletion: DashboardAutoCompletionResponse | null = null;
   @Input() multiDay = false;
   @Input() loading = false;
   @Input() error = false;
   @Input() periodLabel: string | null = null;

   @Output() viewTask = new EventEmitter<number>();
   @Output() viewBreakdown = new EventEmitter<string>();

   expanded = false;

   get tasks(): DashboardAutoCompletedTaskResponse[] {
      return this.autoCompletion?.tasks ?? [];
   }

   get title(): string {
      return this.periodLabel
         ? `Auto-Completed Tasks - ${this.periodLabel}`
         : 'Auto-Completed Tasks';
   }

   get visibleTasks(): DashboardAutoCompletedTaskResponse[] {
      return this.expanded ? this.tasks : this.tasks.slice(0, 5);
   }

   get groupedTasks(): AutoCompletedTaskGroup[] {
      const groups = new Map<string, AutoCompletedTaskGroup>();

      for (const task of this.tasks) {
         const key = task.reportTypeCode;

         const existing = groups.get(key);

         if (existing) {
            existing.count++;

            const reason = this.getReasonLabel(task.reasonCode);

            if (!existing.reasons.includes(reason)) {
               existing.reasons.push(reason);
            }

            continue;
         }

         groups.set(key, {
            reportTypeCode: task.reportTypeCode,
            reportTypeName: task.reportTypeName,
            count: 1,
            reasons: [this.getReasonLabel(task.reasonCode)]
         });
      }

      return [...groups.values()].sort((a, b) =>
         a.reportTypeName.localeCompare(b.reportTypeName)
      );
   }

   get visibleGroupedTasks(): AutoCompletedTaskGroup[] {
      return this.expanded ? this.groupedTasks : this.groupedTasks.slice(0, 5);
   }

   get hasMoreTasks(): boolean {
      const count = this.multiDay ? this.groupedTasks.length : this.tasks.length;

      return count > 5;
   }

   get outcomeRows(): AutoCompletionOutcomeRow[] {
      if (!this.autoCompletion) {
         return [];
      }

      return [
         {
            label: 'Auto-completed',
            value: this.autoCompletion.successful,
            className: 'outcome-primary'
         },
         {
            label: 'Empty reports',
            value: this.autoCompletion.emptyReports,
            className: 'outcome-success'
         },
         {
            label: 'No blocking risks',
            value: this.autoCompletion.noBlockingRisks,
            className: 'outcome-success'
         }
      ];
   }

   get maximumOutcomeValue(): number {
      return Math.max(...this.outcomeRows.map((row) => row.value), 1);
   }

   getOutcomeWidth(value: number): number {
      if (value <= 0) {
         return 0;
      }

      return (value / this.maximumOutcomeValue) * 100;
   }

   getReasonLabel(reasonCode: string): string {
      switch (reasonCode) {
         case 'AUTO_COMPLETED_EMPTY_REPORT':
            return 'Empty report';

         case 'AUTO_COMPLETED_NO_BLOCKING_RISKS':
            return 'No blocking risks';

         default:
            return 'Auto-completed';
      }
   }

   trackByReportTypeCode(_index: number, task: AutoCompletedTaskGroup): string {
      return task.reportTypeCode;
   }

   toggleExpanded(): void {
      this.expanded = !this.expanded;
   }

   onViewTask(taskInstanceId: number): void {
      this.viewTask.emit(taskInstanceId);
   }

   onViewBreakdown(reportTypeCode: string): void {
      this.viewBreakdown.emit(reportTypeCode);
   }
}
